/**
 * Call Metrics Service
 * Handles API calls for call latency and performance metrics
 * Note: Metrics are automatically stored in call_logs.metrics by LiveKit agents
 */

import {
  Metric,
  GetMetricsResponse,
  GetLatencySummaryResponse,
  GetTimelineResponse,
  MetricsFilter,
  LatencyChartDataPoint,
} from "../types/metrics";

// Direct call API URL
const CALL_SERVICE_URL = import.meta.env.VITE_CALL_SERVICE_URL || "";

export const callMetricsService = {

  /**
   * Get metrics for a call
   * @param callId Call log ID
   * @param filter Optional filter parameters
   * @param apiKey Optional API key for authentication
   * @returns Metrics response
   */
  async getMetrics(
    callId: string,
    filter?: MetricsFilter,
    apiKey?: string
  ): Promise<GetMetricsResponse> {
    try {
      const url = new URL(`${CALL_SERVICE_URL}/api/call/${callId}/metrics`);

      // Add filter parameters
      if (filter?.metricTypes && filter.metricTypes.length > 0) {
        url.searchParams.append("metric_types", filter.metricTypes.join(","));
      }

      if (filter?.limit) {
        url.searchParams.append("limit", filter.limit.toString());
      }

      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (apiKey) {
        headers["x-api-key"] = apiKey;
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch metrics");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching metrics:", error);
      throw error;
    }
  },

  /**
   * Get latency summary for a call
   * @param callId Call log ID
   * @param apiKey Optional API key for authentication
   * @returns Latency summary response
   */
  async getLatencySummary(
    callId: string,
    apiKey?: string
  ): Promise<GetLatencySummaryResponse> {
    try {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (apiKey) {
        headers["x-api-key"] = apiKey;
      }

      const response = await fetch(
        `${CALL_SERVICE_URL}/api/call/${callId}/latency-summary`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch latency summary");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching latency summary:", error);
      throw error;
    }
  },

  /**
   * Get response timeline for a call
   * @param callId Call log ID
   * @param apiKey Optional API key for authentication
   * @returns Timeline response
   */
  async getResponseTimeline(
    callId: string,
    apiKey?: string
  ): Promise<GetTimelineResponse> {
    try {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (apiKey) {
        headers["x-api-key"] = apiKey;
      }

      const response = await fetch(
        `${CALL_SERVICE_URL}/api/call/${callId}/response-timeline`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch response timeline");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching response timeline:", error);
      throw error;
    }
  },

  /**
   * Get overall latency statistics across all calls
   * @param apiKey API key for authentication
   * @param limit Maximum number of calls to analyze
   * @param days Number of days to look back
   * @returns Overall latency statistics
   */
  async getOverallLatencyStatistics(
    apiKey: string,
    limit: number = 100,
    days: number = 30
  ): Promise<any> {
    try {
      const url = new URL(`${CALL_SERVICE_URL}/api/analytics/overall-latency`);
      url.searchParams.append("limit", limit.toString());
      url.searchParams.append("days", days.toString());

      const headers: HeadersInit = {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
      };

      const response = await fetch(url.toString(), {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch overall latency statistics");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching overall latency statistics:", error);
      throw error;
    }
  },

  /**
   * Transform metrics into chart data points
   * @param metrics Array of metrics
   * @returns Array of chart data points with relative timestamps
   */
  transformMetricsToChartData(metrics: Metric[]): LatencyChartDataPoint[] {
    const dataPoints: LatencyChartDataPoint[] = [];

    if (metrics.length === 0) {
      return dataPoints;
    }

    // Find the earliest timestamp to use as reference (call start time)
    const startTimestamp = Math.min(...metrics.map(m => m.timestamp));

    // Group metrics by timestamp (rounded to nearest second)
    const groupedMetrics = new Map<number, Partial<LatencyChartDataPoint>>();

    metrics.forEach((metric) => {
      const absoluteTimestamp = Math.floor(metric.timestamp);
      const relativeTimestamp = absoluteTimestamp - Math.floor(startTimestamp); // Duration from start
      const existing = groupedMetrics.get(relativeTimestamp) || { timestamp: relativeTimestamp };

      switch (metric.type) {
        case "stt_metrics":
          existing.stt_duration = metric.duration;
          break;
        case "llm_metrics":
          existing.llm_ttft = metric.ttft;
          existing.llm_duration = metric.duration;
          existing.llm_prompt_tokens = metric.prompt_tokens;
          existing.llm_completion_tokens = metric.completion_tokens;
          existing.llm_total_tokens = metric.total_tokens;
          existing.llm_cached_tokens = metric.prompt_cached_tokens;
          existing.llm_tokens_per_second = metric.tokens_per_second;
          break;
        case "tts_metrics":
          existing.tts_ttfb = metric.ttfb;
          existing.tts_duration = metric.duration;
          break;
        case "vad_metrics":
          existing.vad_inference = metric.inference_duration_total;
          break;
      }

      groupedMetrics.set(relativeTimestamp, existing);
    });

    // Convert map to array and calculate overall response time
    groupedMetrics.forEach((dataPoint) => {
      const stt = dataPoint.stt_duration || 0;
      const llm = dataPoint.llm_duration || 0;
      const tts = dataPoint.tts_ttfb || 0;

      dataPoints.push({
        timestamp: dataPoint.timestamp!,
        stt_duration: dataPoint.stt_duration,
        llm_ttft: dataPoint.llm_ttft,
        llm_duration: dataPoint.llm_duration,
        llm_prompt_tokens: dataPoint.llm_prompt_tokens,
        llm_completion_tokens: dataPoint.llm_completion_tokens,
        llm_total_tokens: dataPoint.llm_total_tokens,
        llm_cached_tokens: dataPoint.llm_cached_tokens,
        llm_tokens_per_second: dataPoint.llm_tokens_per_second,
        tts_ttfb: dataPoint.tts_ttfb,
        tts_duration: dataPoint.tts_duration,
        vad_inference: dataPoint.vad_inference,
        overall_response: stt + llm + tts,
      });
    });

    // Sort by timestamp
    dataPoints.sort((a, b) => a.timestamp - b.timestamp);

    return dataPoints;
  },

  /**
   * Format timestamp to readable string
   * @param timestamp Unix timestamp
   * @param includeDate Whether to include date
   * @returns Formatted time string
   */
  formatTimestamp(timestamp: number, includeDate: boolean = false): string {
    const date = new Date(timestamp * 1000);

    if (includeDate) {
      return date.toLocaleString();
    }

    return date.toLocaleTimeString();
  },

  /**
   * Format relative time (duration from call start) to readable string
   * @param seconds Seconds from start of call
   * @returns Formatted time string (e.g., "0s", "1m 30s", "1h 5m")
   */
  formatRelativeTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  },

  /**
   * Format duration in seconds to readable string
   * @param seconds Duration in seconds
   * @returns Formatted duration string
   */
  formatDuration(seconds: number): string {
    if (seconds < 0.001) {
      return `${(seconds * 1000000).toFixed(0)}µs`;
    } else if (seconds < 1) {
      return `${(seconds * 1000).toFixed(0)}ms`;
    } else {
      return `${seconds.toFixed(2)}s`;
    }
  },

  /**
   * Get color for metric type (for charts)
   * @param metricType Type of metric
   * @returns Hex color code
   */
  getMetricColor(metricType: string): string {
    const colors: Record<string, string> = {
      stt: "#3b82f6", // blue
      llm: "#8b5cf6", // purple
      tts: "#10b981", // green
      vad: "#f59e0b", // amber
      overall: "#ef4444", // red
    };

    return colors[metricType] || "#6b7280";
  },

  /**
   * Calculate percentile from array of values
   * @param values Array of numbers
   * @param percentile Percentile (0-100)
   * @returns Percentile value
   */
  calculatePercentile(values: number[], percentile: number): number {
    if (values.length === 0) return 0;

    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;

    return sorted[Math.max(0, index)];
  },
};
